import {
  Box,
  Button,
  CircularProgress,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ProjectGrid from './project-grid';
import NoRecords from '../../common/application/no-records';
import TabsBar from '../../common/widgets/tabs-bar';
import MultiSelectDropdown from '../../common/widgets/multi-select-dropdown';
import { IPermissions, TabInfo } from '../../common/utils/interfaces';
import {
  ActiveProjectTypes,
  ActiveStatus,
  CompletedStatus
} from '../../common/utils/enum';
import { useAppSelector } from '../../../redux/hooks';
import { GET_PROJECTS } from '../../projects/gql';
import CreateProjectModal from './forms/create-project-modal';
import OutlinedInputComponent from '../../common/widgets/outlined-input';
import { IProjectData } from '../type';
import MultiSelectDropdownToTypes from '../../common/widgets/multi-select-dropdown-type';
import { useDispatch } from 'react-redux';
import { profileDataActions } from '../../../redux/profileData/slice';

const tabs: TabInfo[] = [
  { label: 'Active', value: 'active' },
  { label: 'Completed', value: 'completed' }
];

export const statusOrder = [
  'INITIATED',
  'ONGOING',
  'HYPERCARE_SUPPORT',
  'DISCONTINUED',
  'COMPLETED'
];

const Projects = (): JSX.Element => {
  const { profileData } = useAppSelector((state) => state.profileData);
  const [tab, setTab] = useState('active');
  const [projects, setProjects] = useState<IProjectData[]>([]);
  const [needRefetch, setNeedRefetch] = useState<boolean>(false);
  const [statusOptions, setStatusOptions] = useState(ActiveStatus);
  const [statusList, setStatusList] = useState<string[]>(ActiveStatus);
  const [typesOptions, setTypesOptions] = useState(ActiveProjectTypes);
  const [typesList, setTypesList] = useState<string[]>(ActiveProjectTypes);
  const [isCreateModalVisible, setCreateModalVisible] = useState(false);
  const [permissions, setPermissions] = useState<IPermissions>({
    project: { create: false, read: true, update: false, delete: false },
    project_member: { create: false, read: true, update: false, delete: false }
  });
  const [searchProjects, setSearchProjects] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { data, refetch } = useQuery(GET_PROJECTS, {
    variables: {
      status: statusList,
      searchQuery: searchProjects
    }
  });

  const handleLoadMore = async (): Promise<void> => {
    await refetch({
      status: statusList,
      searchQuery: searchProjects
    });
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTab: string
  ): void => {
    setTab(newTab);
    setIsLoading(true);
  };

  const handleStatusChange = ({
    target: { value }
  }: SelectChangeEvent<typeof statusList>): void => {
    const updatedValue: string[] = typeof value === 'string' ? [value] : value;
    setStatusList(updatedValue);
    setProjects([]);
    setIsLoading(true);
  };

  const handleTypesChange = ({
    target: { value }
  }: SelectChangeEvent<typeof typesList>): void => {
    const updatedValue: string[] = typeof value === 'string' ? [value] : value;
    setTypesList(updatedValue);
    setProjects(
      data?.getProjects.filter(
        (project: IProjectData) =>
          updatedValue.includes(project.projectType) ||
          project.projectType === null ||
          project.projectType === undefined
      )
    );
  };

  const handleDeleteChip = (deletedChip: string): void => {
    setStatusList((chips) => chips.filter((chip) => chip !== deletedChip));
    setProjects([]);
  };

  const createProjectCallback = (projectId: string): void => {
    setNeedRefetch(!needRefetch);
  };

  const closeRequestForm = (): void => {
    setCreateModalVisible(false);
  };

  useEffect(() => {
    if (profileData?.permissions) {
      setPermissions(profileData.permissions);
    }
  }, [profileData]);

  useEffect(() => {
    dispatch(profileDataActions.getProfileData());
  }, [projects]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await refetch();
      setIsLoading(false);
    };
    void fetchData();
  }, [needRefetch]);

  useEffect(() => {
    setStatusList(tab === 'active' ? ActiveStatus : ['COMPLETED']);
    setStatusOptions(tab === 'active' ? ActiveStatus : CompletedStatus);
    setTypesList(ActiveProjectTypes);
    setTypesOptions(ActiveProjectTypes);
    setSearchProjects('');
  }, [tab]);

  useEffect(() => {
    setProjects([]);
    void handleLoadMore();
  }, [statusOptions, searchProjects, statusList]);

  useEffect(() => {
    setIsLoading(true);
    if (data) {
      const newProjects = data?.getProjects;
      setProjects(newProjects);
      setIsLoading(false);
    }
  }, [data]);

  useEffect(() => {
    setIsLoading(true);
  }, [searchProjects]);

  return (
    <Box
      data-testid={'projects-container'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        allignItems: 'flex-start',
        padding: '21px 20px',
        height: 'calc(100vh - 75px)',
        margin: '0px 22px 20px 22px',
        borderRadius: '12px',
        gap: '10px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          color: '#46464F',
          alignItems: 'flex-start'
        }}
      >
        <Typography sx={{ fontSize: '28px', fontWeight: 700 }}>
          {`Projects`}
        </Typography>
        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
          {'User Management > Projects'}
        </Typography>
      </Box>

      <Box
        sx={{
          color: '#FFFFFF',
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        {permissions.project.create ? (
          <Button
            variant="contained"
            onClick={() => setCreateModalVisible(true)}
            sx={{
              borderRadius: '100px',
              fontSize: '14px',
              fontWeight: 500,
              gap: '8px',
              height: '40px',
              letterSpacing: 0.25,
              lineHeight: '20px',
              padding: '10px 24px 10px 16px',
              textTransform: 'capitalize'
            }}
          >
            <AddIcon sx={{ height: '18px', width: '18px' }} />
            {`Create Project`}
          </Button>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '20px',
          justifyContent: 'space-between',
          marginBottom: '15px'
        }}
      >
        <Box>
          <TabsBar
            tabs={tabs}
            tab={tab}
            handleTabChange={handleTabChange}
            width={'180px'}
          />
          <Box
            sx={{
              height: '1px',
              background: '#E0E2EC'
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            '@media (min-width: 502px)': {
              marginLeft: 'auto'
            }
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <MultiSelectDropdownToTypes
              label={'Filter by Project Type'}
              values={typesList}
              options={typesOptions}
              allowChips={false}
              handleStatusChange={handleTypesChange}
              handleDeleteChip={handleDeleteChip}
            />
          </Box>
          <Box sx={{ display: 'flex' }}>
            <MultiSelectDropdown
              label={'Filter by Status'}
              values={statusList}
              options={statusOptions}
              allowChips={true}
              handleStatusChange={handleStatusChange}
              handleDeleteChip={handleDeleteChip}
            />
          </Box>

          <Box
            sx={{
              display: 'flex'
            }}
          >
            <OutlinedInputComponent
              value={searchProjects}
              onChangeCallback={(value) => setSearchProjects(value)}
              placeholder={'Search'}
              height={'48px'}
              width={'100%'}
              borderRadius={'8px'}
              fontSize={'16px'}
              fontWeight={'500'}
              color={'#5F6368'}
            />
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
            height: 'calc(100vh - 150px)'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: projects.length === 0 ? 'center' : 'flex-start',
              overflowY: projects.length === 0 ? 'none' : 'auto',
              height: 'calc(100vh - 150px)',
              '&::-webkit-scrollbar': {
                width: '0.4em'
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2)',
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '10px'
              },
              paddingRight: '5px'
            }}
          >
            {projects.length === 0 ? (
              <NoRecords message={'No Projects Available'} />
            ) : (
              <ProjectGrid projects={projects} />
            )}
          </Box>
        </>
      )}
      <CreateProjectModal
        createProjectCallback={(value: string) => {
          createProjectCallback(value);
        }}
        closeRequestForm={closeRequestForm}
        isCreateModalVisible={isCreateModalVisible}
      />
    </Box>
  );
};

export default Projects;
